import {
  Box, Button, Flex, HStack, Input, InputGroup, InputLeftElement, Select,
  Table, Thead, Tbody, Tr, Th, Td,
  Text, Badge, Switch, IconButton,
  useColorModeValue, Icon,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, TimeIcon } from "@chakra-ui/icons";
import { Search } from "lucide-react";
import { useUsersAdmin } from "./useUsersAdmin";
import { UserEditModal } from "./UserEditModal";
import { UserCreateModal } from "./UserCreateModal";
import { UserAccessLogModal } from "./UserAccessLogModal";
import { ChildrenInteractionBlocker } from "../../../components/overlays/ChildrenInteractionBlocker";
import { PERMISSIONS } from "../../../config/permissions";
import { ErrorState } from "../../../components/ErrorState";
import { ListPagination } from "../../rh/onboarding/components/ListPagination";
import { OnflyLoading } from "../../../tools/OnflyLoading";

export function UsersAdminPage() {
  const titleColor    = useColorModeValue("#434347", "#e8edf2");
  const descColor     = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor   = useColorModeValue("#84858c", "#718096");
  const thColor       = useColorModeValue("#84858c", "#718096");
  const cardBg        = useColorModeValue("white", "#0f2040");
  const cardBorder    = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const theadBg       = useColorModeValue("rgba(0,158,251,0.04)", "rgba(0,158,251,0.06)");
  const rowHoverBg    = useColorModeValue("rgba(0,158,251,0.025)", "rgba(0,158,251,0.04)");
  const inputBg       = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder   = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");

  const {
    users,
    roles,
    loading,
    error,
    search,
    setSearch,
    isActiveFilter,
    setIsActiveFilter,
    page,
    totalPages,
    setPage,
    isEditOpen,
    setIsEditOpen,
    isLogsOpen,
    setIsLogsOpen,
    isCreateOpen,
    setIsCreateOpen,
    selectedUser,
    handleToggleActive,
    handleEditOpen,
    handleLogsOpen,
    refreshList,
  } = useUsersAdmin();

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
            Gerenciamento de Usuários
          </Text>
          <Text fontSize="sm" color={descColor}>
            Gerencie colaboradores, papéis e status de acesso à plataforma.
          </Text>
        </Box>
        <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_USERS]}>
          <Button flexShrink={0}
            leftIcon={<AddIcon />}
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
          >
            Cadastrar usuário
          </Button>
        </ChildrenInteractionBlocker>
      </Flex>

      <HStack spacing={3} mb={6} maxW="680px">
        <InputGroup flex={1}>
          <InputLeftElement pointerEvents="none">
            <Icon as={Search} boxSize={4} color={subtleColor} />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            bg={inputBg}
            borderColor={inputBorder}
            borderRadius="8px"
            fontSize="sm"
            _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
            _placeholder={{ color: subtleColor }}
          />
        </InputGroup>
        <Select
          placeholder="Todos os status"
          w="200px"
          value={isActiveFilter === undefined ? "" : String(isActiveFilter)}
          onChange={(e) => {
            const val = e.target.value;
            setIsActiveFilter(val === "" ? undefined : val === "true");
          }}
          bg={inputBg}
          borderColor={inputBorder}
          borderRadius="8px"
          fontSize="sm"
          _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
        >
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </Select>
      </HStack>

      {loading ? (
        <Flex justify="center" align="center" py={16}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : error ? (
        <ErrorState onRetry={refreshList} />
      ) : users.length === 0 ? (
        <Text color={subtleColor} fontSize="sm">Nenhum usuário encontrado para o filtro aplicado.</Text>
      ) : (
        <Box
          bg={cardBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="0px 4px 8px 0px rgba(14, 61, 93, 0.08)"
          overflow="hidden"
          overflowX="auto"
        >
          <Table variant="simple" minW="560px">
            <Thead bg={theadBg}>
              <Tr>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Nome / E-mail</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Papéis</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>País</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Status</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" textTransform="none" py={3}>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user) => (
                <Tr key={user.id} _hover={{ bg: rowHoverBg }} transition="background 0.12s">
                  <Td borderColor={cardBorder} py={3.5}>
                    <Box>
                      <Text fontWeight={600} fontSize="sm" color={titleColor}>{user.name}</Text>
                      <Text fontSize="xs" color={descColor} mt={0.5}>{user.email}</Text>
                    </Box>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={1.5} wrap="wrap">
                      {user.roles.length === 0 ? (
                        <Text fontSize="xs" color={subtleColor} fontStyle="italic">Nenhum</Text>
                      ) : (
                        user.roles.map((r) => (
                          <Badge
                            key={r}
                            borderRadius="50px"
                            bg="rgba(0,158,251,0.12)"
                            color="#009efb"
                            fontSize="11px"
                            fontWeight={600}
                            px={2.5}
                            py={0.5}
                          >
                            {r}
                          </Badge>
                        ))
                      )}
                    </HStack>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{user.country || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={2}>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_USERS]}>
                        <Switch
                          colorScheme="blue"
                          isChecked={user.is_active}
                          onChange={() => handleToggleActive(user.id)}
                          size="sm"
                        />
                      </ChildrenInteractionBlocker>
                      <Badge
                        borderRadius="50px"
                        fontSize="11px"
                        fontWeight={600}
                        px={2.5}
                        py={0.5}
                        bg={user.is_active ? "rgba(0,164,110,0.12)" : "rgba(220,53,69,0.10)"}
                        color={user.is_active ? "#00a46e" : "#dc3545"}
                      >
                        {user.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={0}>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_USERS]}>
                        <IconButton
                          aria-label="Editar usuário"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          color="#009efb"
                          _hover={{ bg: "rgba(0,158,251,0.08)" }}
                          onClick={() => handleEditOpen(user)}
                        />
                      </ChildrenInteractionBlocker>
                      <IconButton
                        aria-label="Histórico de acesso"
                        icon={<TimeIcon />}
                        size="sm"
                        variant="ghost"
                        color={subtleColor}
                        _hover={{ bg: "rgba(0,158,251,0.08)", color: "#009efb" }}
                        onClick={() => handleLogsOpen(user)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <UserCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        availableRoles={roles}
        onSuccess={refreshList}
      />

      <UserEditModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        user={selectedUser}
        availableRoles={roles}
        onSuccess={refreshList}
      />

      <UserAccessLogModal
        isOpen={isLogsOpen}
        onClose={() => setIsLogsOpen(false)}
        user={selectedUser}
      />
    </Box>
  );
}
